import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    AlertTriangle,
    ArrowRight,
    Banknote,
    BarChart3,
    Boxes,
    ClipboardList,
    Factory,
    FileText,
    Package,
    Receipt,
    RefreshCw,
    TrendingUp,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useModuleLoader } from '../context/ModuleLoaderContext';
import { useNavigationProfile } from '../hooks/useNavigationProfile';
import { useSettings } from '../context/SettingsContext';
import { api } from '../services/api';
import type {
    DashboardRecentActivity,
    ExecutiveDashboardChart,
    ExecutiveDashboardChartSeries,
    ExecutiveDashboardDrillLink,
    ExecutiveDashboardResponse,
    ExecutiveDashboardTopList,
    ExecutiveModuleDashboardResponse,
} from '../services/types';
import { isEnabledFlag } from '../lib/featureFlags';
import { canonicalizeNavigationPath, isNavigationPathAllowed } from '../lib/navigationPaths';

const ROUTE_LABELS: Record<string, string> = {
    '/': 'Home',
    '/legacy/inquiries': 'Inquiries',
    '/inquiries': 'Inquiries',
    '/legacy/estimates': 'Estimates',
    '/legacy/orders': 'Sales Orders',
    '/sales/orders': 'Sales Orders',
    '/legacy/delivery': 'Delivery Notes',
    '/delivery': 'Delivery Notes',
    '/legacy/invoices': 'Invoices',
    '/sales/invoices': 'Invoices',
    '/legacy/customers': 'Customers',
    '/legacy/suppliers': 'Suppliers',
    '/purchasing/orders': 'Purchase Orders',
    '/purchasing/grn': 'Goods Received Notes',
    '/purchasing/bills': 'Supplier Bills',
    '/financials': 'Financials',
    '/reports': 'Reports',
    '/manufacturing': 'Work Orders',
    '/inventory': 'Inventory',
    '/materials': 'Materials',
    '/executive/overview': 'Executive Overview',
    '/executive/finance': 'Finance Hub',
    '/executive/sales': 'Sales Hub',
    '/executive/production': 'Production Hub',
    '/executive/inventory': 'Inventory Hub',
    '/executive/purchasing': 'Purchasing Hub',
};

const MODULE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
    finance: Banknote,
    sales: TrendingUp,
    production: Factory,
    inventory: Boxes,
    purchasing: Receipt,
};

const DEFAULT_ROLE_HOME_LAYOUT = [
    {
        role: 'admin',
        cards: [
            { title: 'Sales & CRM', paths: ['/inquiries', '/legacy/estimates', '/sales/orders'] },
            { title: 'Purchasing', paths: ['/purchasing/orders', '/purchasing/grn', '/purchasing/bills'] },
            { title: 'Finance', paths: ['/financials', '/sales/invoices', '/reports'] },
            { title: 'Production', paths: ['/manufacturing', '/inventory', '/materials'] },
        ],
    },
    {
        role: 'sales',
        cards: [
            { title: 'Leads to Quote', paths: ['/inquiries', '/legacy/estimates'] },
            { title: 'Order to Cash', paths: ['/sales/orders', '/delivery', '/sales/invoices'] },
            { title: 'Customers', paths: ['/legacy/customers'] },
        ],
    },
    {
        role: 'purchasing',
        cards: [
            { title: 'Procurement', paths: ['/purchasing/orders'] },
            { title: 'Receiving', paths: ['/purchasing/grn'] },
            { title: 'Supplier Billing', paths: ['/purchasing/bills'] },
        ],
    },
    {
        role: 'finance',
        cards: [
            { title: 'Financial Operations', paths: ['/financials'] },
            { title: 'Invoicing', paths: ['/sales/invoices'] },
            { title: 'Reporting', paths: ['/reports'] },
        ],
    },
    {
        role: 'production',
        cards: [
            { title: 'Work Orders', paths: ['/manufacturing'] },
            { title: 'Stock Control', paths: ['/inventory', '/materials'] },
            { title: 'Delivery Snapshot', paths: ['/delivery'] },
        ],
    },
];

const QUICK_ACTIONS = [
    { label: 'Sales Orders', description: 'Review the order pipeline', to: '/sales/orders', icon: ClipboardList },
    { label: 'Financials', description: 'Open ledger and statements', to: '/financials', icon: Banknote },
    { label: 'Estimates', description: 'Review quote workload', to: '/legacy/estimates', icon: FileText },
    { label: 'Reports', description: 'Open operational reports', to: '/reports', icon: BarChart3 },
    { label: 'Work Orders', description: 'View production pipeline', to: '/manufacturing', icon: Factory },
];

const EXECUTIVE_SECTIONS = [
    { key: 'overview', label: 'Overview', to: '/executive/overview', icon: BarChart3 },
    { key: 'finance', label: 'Finance', to: '/executive/finance', icon: Banknote },
    { key: 'sales', label: 'Sales', to: '/executive/sales', icon: TrendingUp },
    { key: 'production', label: 'Production', to: '/executive/production', icon: Factory },
    { key: 'inventory', label: 'Inventory', to: '/executive/inventory', icon: Boxes },
    { key: 'purchasing', label: 'Purchasing', to: '/executive/purchasing', icon: Receipt },
] as const;

type ExecutiveSectionKey = (typeof EXECUTIVE_SECTIONS)[number]['key'];

function toFriendlyRouteLabel(path: string) {
    if (!path) return 'Unknown';
    if (ROUTE_LABELS[path]) return ROUTE_LABELS[path];
    const plain = path.replace(/^\/+/, '').replace(/[-_]/g, ' ');
    if (!plain) return 'Home';
    return plain
        .split('/')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' / ');
}

function formatValue(value: number | string | null | undefined, format = 'number') {
    if (value === null || value === undefined || value === '') return '—';
    if (format === 'text') return String(value);
    const numeric = Number(value || 0);
    if (format === 'money') {
        return `AED ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numeric)}`;
    }
    if (format === 'percent') {
        return `${numeric.toFixed(1)}%`;
    }
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numeric);
}

function formatTooltipValue(value: unknown, format = 'number') {
    if (Array.isArray(value)) {
        return value.join(' - ');
    }
    if (typeof value === 'number' || typeof value === 'string' || value === null || value === undefined) {
        return formatValue(value, format);
    }
    return String(value);
}

function formatRelativeTime(iso?: string | null) {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 60) return `${Math.max(diffMinutes, 1)}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}

function statusClasses(status: string) {
    if (status === 'danger') return 'border-rose-200 bg-rose-50 text-rose-800';
    if (status === 'attention') return 'border-amber-200 bg-amber-50 text-amber-800';
    return 'border-emerald-200 bg-emerald-50 text-emerald-800';
}

function severityClasses(severity: string) {
    if (severity === 'danger') return 'border-rose-200 bg-rose-50 text-rose-800';
    if (severity === 'warning') return 'border-amber-200 bg-amber-50 text-amber-800';
    return 'border-sky-200 bg-sky-50 text-sky-800';
}

function getExecutiveSectionFromPath(pathname: string): ExecutiveSectionKey {
    if (!pathname || pathname === '/') return 'overview';
    if (pathname === '/executive' || pathname === '/executive/') return 'overview';
    const match = pathname.match(/^\/executive\/([^/?#]+)/);
    const value = String(match?.[1] || '').toLowerCase();
    return (EXECUTIVE_SECTIONS.find((section) => section.key === value)?.key || 'overview') as ExecutiveSectionKey;
}

function formatAxisTick(value: unknown, series: ExecutiveDashboardChartSeries[], axis: 'primary' | 'secondary') {
    const axisFormats = series.filter((item) => (item.axis || 'primary') === axis).map((item) => item.format || 'number');
    if (axisFormats.includes('money')) {
        return `${Math.round(Number(value || 0) / 1000)}k`;
    }
    if (axisFormats.includes('percent')) {
        return `${Number(value || 0).toFixed(0)}%`;
    }
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function ExecutiveSectionNav({ activeSection }: { activeSection: ExecutiveSectionKey }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            {EXECUTIVE_SECTIONS.map((section) => {
                const Icon = section.icon;
                const active = section.key === activeSection;
                return (
                    <Button
                        key={section.key}
                        asChild
                        variant={active ? 'default' : 'secondary'}
                        size="sm"
                        className={active ? 'gap-2 bg-slate-950 text-white hover:bg-slate-900' : 'gap-2'}
                    >
                        <Link to={section.to}>
                            <Icon className="h-4 w-4" />
                            {section.label}
                        </Link>
                    </Button>
                );
            })}
        </div>
    );
}

function ExecutiveChartCard({ chart }: { chart: ExecutiveDashboardChart }) {
    const chartData = useMemo(
        () => (chart.data || []).map((point) => ({ label: point.label, ...(point.values || {}) })),
        [chart.data]
    );
    const hasSecondaryAxis = chart.series.some((item) => (item.axis || 'primary') === 'secondary');
    const seriesMap = useMemo(() => new Map(chart.series.map((item) => [item.key, item])), [chart.series]);

    return (
        <Card className="border-slate-200 shadow-sm">
            <CardHeader>
                <CardTitle>{chart.title}</CardTitle>
                <CardDescription>{chart.description}</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="h-[320px] w-full">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                        <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" />
                            <YAxis yAxisId="primary" tickFormatter={(value) => formatAxisTick(value, chart.series, 'primary')} />
                            {hasSecondaryAxis && (
                                <YAxis yAxisId="secondary" orientation="right" tickFormatter={(value) => formatAxisTick(value, chart.series, 'secondary')} />
                            )}
                            <Tooltip
                                formatter={(value, name) => {
                                    const seriesMeta = seriesMap.get(String(name));
                                    return formatTooltipValue(value, seriesMeta?.format || 'number');
                                }}
                            />
                            <Legend />
                            {chart.series.map((series) => {
                                const axis = (series.axis || 'primary') === 'secondary' ? 'secondary' : 'primary';
                                if (series.kind === 'line') {
                                    return <Line key={series.key} yAxisId={axis} type="monotone" dataKey={series.key} name={series.label} stroke={series.color || '#0f766e'} strokeWidth={2} dot={false} />;
                                }
                                if (series.kind === 'area') {
                                    return <Area key={series.key} yAxisId={axis} type="monotone" dataKey={series.key} name={series.label} fill={series.color || '#0f766e'} stroke={series.color || '#0f766e'} fillOpacity={0.25} />;
                                }
                                return <Bar key={series.key} yAxisId={axis} dataKey={series.key} name={series.label} fill={series.color || '#0f766e'} radius={[4, 4, 0, 0]} />;
                            })}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
}

function ExecutiveTopListCard({ list }: { list: ExecutiveDashboardTopList }) {
    return (
        <Card className="border-slate-200 shadow-sm">
            <CardHeader>
                <CardTitle>{list.title}</CardTitle>
                <CardDescription>{list.description || 'Ranked operational detail.'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {(list.items || []).length > 0 ? (
                    list.items.map((item) => {
                        const content = (
                            <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 p-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-slate-900">{item.label}</p>
                                    <p className="truncate text-xs text-slate-500">{item.detail || '—'}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-semibold text-slate-900">{formatValue(item.value, item.format || 'number')}</p>
                                    {item.secondary_value !== null && item.secondary_value !== undefined && (
                                        <p className="text-xs text-slate-400">{formatValue(item.secondary_value, item.secondary_format || 'number')}</p>
                                    )}
                                </div>
                            </div>
                        );
                        if (item.route) {
                            return (
                                <Link key={item.id} to={item.route} className="block">
                                    {content}
                                </Link>
                            );
                        }
                        return <div key={item.id}>{content}</div>;
                    })
                ) : (
                    <p className="text-sm text-slate-500">No ranked items for this selection.</p>
                )}
            </CardContent>
        </Card>
    );
}

function ExecutiveDrillLinks({ links }: { links: ExecutiveDashboardDrillLink[] }) {
    if (!links.length) return null;
    return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {links.map((link) => (
                <Button key={`${link.route}-${link.label}`} asChild variant="secondary" className="h-auto justify-between px-4 py-3">
                    <Link to={link.route}>
                        <span className="min-w-0 text-left">
                            <span className="block truncate text-sm font-semibold">{link.label}</span>
                            <span className="block truncate text-xs text-slate-500">{link.note || 'Open related workspace'}</span>
                        </span>
                        <ArrowRight className="h-4 w-4 shrink-0" />
                    </Link>
                </Button>
            ))}
        </div>
    );
}

function useAllowedNavigationRoots() {
    const { activeModules } = useModuleLoader();
    return useMemo(
        () =>
            new Set<string>([
                '/',
                '/reports',
                '/settings',
                '/help',
                ...activeModules.flatMap((module) => (module.menus || []).map((menu) => canonicalizeNavigationPath(menu.path))),
            ]),
        [activeModules]
    );
}

function RoleWorkspaceHome() {
    const { user } = useAuth();
    const { getSetting } = useSettings();
    const navigationV2Enabled = isEnabledFlag(getSetting('ui_navigation_v2_enabled'));
    const { profile } = useNavigationProfile(navigationV2Enabled);
    const allowedNavigationRoots = useAllowedNavigationRoots();

    const roleWorkspaceCards = useMemo(() => {
        const role = String(user?.role || 'admin').toLowerCase();
        const profileCards = (profile.home_role_layout || []).find((layout) => String(layout.role || '').toLowerCase() === role)?.cards;
        const sourceCards = (Array.isArray(profileCards) && profileCards.length > 0
            ? profileCards
            : DEFAULT_ROLE_HOME_LAYOUT.find((layout) => layout.role === role)?.cards || DEFAULT_ROLE_HOME_LAYOUT[0].cards) as Array<{ title: string; paths: string[] }>;
        return sourceCards
            .map((card) => ({
                ...card,
                paths: (card.paths || []).filter((path) => isNavigationPathAllowed(path, allowedNavigationRoots)),
            }))
            .filter((card) => card.paths.length > 0);
    }, [allowedNavigationRoots, profile.home_role_layout, user?.role]);

    const recentNavigation = useMemo(() => {
        const seen = new Set<string>();
        return (profile.recent_paths || [])
            .filter((entry) => entry.path && entry.path !== '/' && isNavigationPathAllowed(entry.path, allowedNavigationRoots))
            .filter((entry) => {
                const key = canonicalizeNavigationPath(entry.path);
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .slice(0, 6);
    }, [allowedNavigationRoots, profile.recent_paths]);

    const visibleQuickActions = useMemo(
        () => QUICK_ACTIONS.filter((action) => isNavigationPathAllowed(action.to, allowedNavigationRoots)),
        [allowedNavigationRoots]
    );

    return (
        <div className="space-y-6 p-6">
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-950">
                    Welcome <span className="font-normal text-slate-500">{user?.full_name || user?.username || 'User'}</span>
                </h1>
                <p className="mt-1 text-sm text-slate-500">Your workspace is ready.</p>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <Card className="border-slate-200 shadow-sm xl:col-span-2">
                    <CardHeader>
                        <CardTitle>Role Workspace</CardTitle>
                        <CardDescription>Daily flows for your role.</CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {roleWorkspaceCards.map((card) => (
                            <div key={card.title} className="rounded-lg border border-slate-100 p-4">
                                <h3 className="text-sm font-semibold text-slate-900">{card.title}</h3>
                                <div className="mt-3 space-y-2">
                                    {card.paths.slice(0, 3).map((path) => (
                                        <Button key={`${card.title}-${path}`} asChild size="sm" variant="secondary" className="w-full justify-between">
                                            <Link to={path}>
                                                <span className="truncate">{toFriendlyRouteLabel(path)}</span>
                                                <ArrowRight className="h-4 w-4" />
                                            </Link>
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                        <CardTitle>Recent Views</CardTitle>
                        <CardDescription>Recently used pages.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {recentNavigation.length > 0 ? (
                            recentNavigation.map((entry) => (
                                <Link key={`${entry.path}-${entry.last_used_at}`} to={entry.path} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 hover:bg-slate-50">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-800">{entry.label && !entry.label.startsWith('/') ? entry.label : toFriendlyRouteLabel(entry.path)}</p>
                                        <p className="truncate text-xs text-slate-500">{toFriendlyRouteLabel(entry.path)}</p>
                                    </div>
                                    <ArrowRight className="h-4 w-4 text-slate-400" />
                                </Link>
                            ))
                        ) : (
                            <p className="text-sm text-slate-500">No recent views yet.</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                    <CardDescription>Common workflows.</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {visibleQuickActions.map((action) => (
                        <Link key={action.label} to={action.to} className="flex items-center gap-4 rounded-lg border border-slate-100 p-4 hover:bg-slate-50">
                            <span className="rounded-full bg-slate-100 p-3">
                                <action.icon className="h-5 w-5 text-slate-700" />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-sm font-semibold text-slate-900">{action.label}</span>
                                <span className="block truncate text-xs text-slate-500">{action.description}</span>
                            </span>
                        </Link>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
}

function ExecutiveDashboardHome() {
    const location = useLocation();
    const activeSection = useMemo(() => getExecutiveSectionFromPath(location.pathname), [location.pathname]);
    const isOverview = activeSection === 'overview';
    const [dashboard, setDashboard] = useState<ExecutiveDashboardResponse | null>(null);
    const [moduleDashboard, setModuleDashboard] = useState<ExecutiveModuleDashboardResponse | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
    const [selectedPeriodMode, setSelectedPeriodMode] = useState<'ytd' | 'month'>('ytd');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const moduleSupportsPeriodToggle = !isOverview && (activeSection === 'production' || activeSection === 'inventory');

    const loadDashboard = useCallback(async (section: ExecutiveSectionKey, year?: number, opts?: { silent?: boolean }, periodMode: 'ytd' | 'month' = 'ytd') => {
        if (!opts?.silent) setLoading(true);
        setError(null);
        try {
            if (section === 'overview') {
                const data = await api.reports.getExecutiveDashboard(year ? { year } : undefined);
                setDashboard(data);
                setModuleDashboard(null);
            } else {
                const filters: { year?: number; period_mode?: 'ytd' | 'month' } = {};
                if (year) filters.year = year;
                if (section === 'production' || section === 'inventory') {
                    filters.period_mode = periodMode;
                }
                const data = await api.reports.getExecutiveModuleDashboard(section, Object.keys(filters).length ? filters : undefined);
                setModuleDashboard(data);
                setDashboard(null);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        } finally {
            if (!opts?.silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeSection === 'overview') {
            setModuleDashboard(null);
            return;
        }
        setDashboard(null);
        setModuleDashboard(null);
    }, [activeSection]);

    useEffect(() => {
        loadDashboard(activeSection, selectedYear, undefined, selectedPeriodMode);
    }, [activeSection, loadDashboard, selectedPeriodMode, selectedYear]);

    const activeSurface = isOverview ? dashboard : moduleDashboard;
    const primaryKpis = isOverview ? dashboard?.kpis ?? [] : moduleDashboard?.kpis ?? [];
    const activePeriodMode = moduleDashboard?.period_mode === 'month' ? 'month' : selectedPeriodMode;
    const visibleYears = isOverview
        ? dashboard?.available_years?.length
            ? dashboard.available_years
            : selectedYear
              ? [selectedYear]
              : []
        : moduleDashboard?.available_years?.length
          ? moduleDashboard.available_years
          : selectedYear
            ? [selectedYear]
            : [];
    const currentMonthLabel = useMemo(() => {
        const year = (isOverview ? dashboard?.year : moduleDashboard?.year) || selectedYear || new Date().getUTCFullYear();
        const monthIndex = new Date().getUTCMonth();
        return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
            new Date(Date.UTC(year, monthIndex, 1))
        );
    }, [dashboard?.year, isOverview, moduleDashboard?.year, selectedYear]);

    const summaryTitle = isOverview ? 'ERP Command Dashboard' : moduleDashboard?.title || 'Executive Module Dashboard';
    const summaryDescription = isOverview
        ? `Balanced view of finance, sales, production, inventory, and purchasing for ${dashboard?.year || selectedYear || 'the current year'}.`
        : moduleDashboard?.summary || 'Module-specific executive analytics.';
    const activeYear = (isOverview ? dashboard?.year : moduleDashboard?.year) || selectedYear || '';
    const activeStatus = moduleDashboard?.status || 'ok';

    return (
        <div className="space-y-6 p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div>
                    <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Executive Overview</p>
                    <h1 className="mt-1 text-4xl font-bold tracking-tight text-slate-950">{summaryTitle}</h1>
                    <p className="mt-2 max-w-3xl text-sm text-slate-500">{summaryDescription}</p>
                    {!isOverview && (
                        <span className={`mt-3 inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusClasses(activeStatus)}`}>
                            {activeStatus}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <ExecutiveSectionNav activeSection={activeSection} />
                    {moduleSupportsPeriodToggle && (
                        <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-1 shadow-sm">
                            <Button
                                type="button"
                                size="sm"
                                variant={activePeriodMode === 'ytd' ? 'default' : 'ghost'}
                                className={activePeriodMode === 'ytd' ? 'bg-slate-950 text-white hover:bg-slate-900' : 'text-slate-600'}
                                onClick={() => setSelectedPeriodMode('ytd')}
                            >
                                YTD
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant={activePeriodMode === 'month' ? 'default' : 'ghost'}
                                className={activePeriodMode === 'month' ? 'bg-slate-950 text-white hover:bg-slate-900' : 'text-slate-600'}
                                onClick={() => setSelectedPeriodMode('month')}
                            >
                                Current Month
                            </Button>
                        </div>
                    )}
                    <select
                        aria-label="Dashboard year"
                        value={selectedYear || activeYear}
                        onChange={(event) => setSelectedYear(Number(event.target.value))}
                        className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm"
                    >
                        {visibleYears.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                    <Button type="button" variant="secondary" size="sm" onClick={() => loadDashboard(activeSection, selectedYear, { silent: true }, selectedPeriodMode)} className="gap-2">
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {loading && !activeSurface
                    ? Array.from({ length: 8 }).map((_, index) => (
                        <div key={index} className="h-32 animate-pulse rounded-lg border border-slate-200 bg-white" />
                    ))
                    : primaryKpis.map((item) => {
                        const change = item.change_percent;
                        return (
                            <div key={item.key} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</p>
                                        <p className="mt-2 text-2xl font-bold tracking-tight text-slate-950">{formatValue(item.value, item.format)}</p>
                                    </div>
                                    <BarChart3 className="h-5 w-5 text-slate-400" />
                                </div>
                                <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                                    <span className="truncate text-slate-500">{item.basis}</span>
                                    {change !== null && change !== undefined && (
                                        <span className={change >= 0 ? 'font-semibold text-emerald-700' : 'font-semibold text-rose-700'}>
                                            {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>

            {loading && !activeSurface && (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                    <div className="h-80 animate-pulse rounded-lg border border-slate-200 bg-white" />
                    <div className="h-80 animate-pulse rounded-lg border border-slate-200 bg-white" />
                </div>
            )}

            {isOverview ? (
                <>
                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                        <Card className="border-slate-200 shadow-sm xl:col-span-2">
                            <CardHeader>
                                <CardTitle>YTD Trend</CardTitle>
                                <CardDescription>Sales compared with {dashboard?.compare_year}; pipeline volume overlaid.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[340px] w-full">
                                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                                        <ComposedChart data={dashboard?.monthly_trends || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="month_label" />
                                            <YAxis yAxisId="money" tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                                            <YAxis yAxisId="count" orientation="right" allowDecimals={false} />
                                            <Tooltip formatter={(value, name) => (String(name).toLowerCase().includes('sales') ? formatTooltipValue(value, 'money') : formatTooltipValue(value))} />
                                            <Legend />
                                            <Bar yAxisId="money" dataKey="compare_sales" name={`${dashboard?.compare_year || ''} Sales`} fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                                            <Bar yAxisId="money" dataKey="sales" name={`${dashboard?.year || ''} Sales`} fill="#0f766e" radius={[4, 4, 0, 0]} />
                                            <Area yAxisId="count" type="monotone" dataKey="estimates" name="Estimates" fill="#bae6fd" stroke="#0284c7" fillOpacity={0.35} />
                                            <Line yAxisId="count" type="monotone" dataKey="orders" name="Orders" stroke="#f97316" strokeWidth={2} dot={false} />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle>Attention</CardTitle>
                                <CardDescription>Items that need executive visibility.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {(dashboard?.attention_items || []).length > 0 ? (
                                    dashboard!.attention_items.map((item) => (
                                        <Link
                                            key={item.id}
                                            to={item.route || '/reports'}
                                            className={`block rounded-lg border p-3 ${severityClasses(item.severity)}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold">{item.title}</p>
                                                    <p className="mt-1 text-xs opacity-80">{item.detail}</p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">No attention items right now.</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <Card className="border-slate-200 shadow-sm">
                        <CardHeader>
                            <CardTitle>Current Month Daywise Trend</CardTitle>
                            <CardDescription>Daily sales and pipeline movement for {currentMonthLabel}.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[320px] w-full">
                                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                                    <ComposedChart data={dashboard?.current_month_daily_trends || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="day_label" />
                                        <YAxis yAxisId="money" tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                                        <YAxis yAxisId="count" orientation="right" allowDecimals={false} />
                                        <Tooltip formatter={(value, name) => (String(name).toLowerCase().includes('sales') ? formatTooltipValue(value, 'money') : formatTooltipValue(value))} />
                                        <Legend />
                                        <Bar yAxisId="money" dataKey="sales" name="Sales" fill="#0f766e" radius={[4, 4, 0, 0]} />
                                        <Area yAxisId="count" type="monotone" dataKey="inquiries" name="Inquiries" fill="#bfdbfe" stroke="#2563eb" fillOpacity={0.35} />
                                        <Line yAxisId="count" type="monotone" dataKey="orders" name="Orders" stroke="#f97316" strokeWidth={2} dot={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
                        {(dashboard?.module_sections || []).map((section) => {
                            const Icon = MODULE_ICONS[section.module_key] || Package;
                            return (
                                <div key={section.module_key} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <span className="rounded-md bg-slate-100 p-2">
                                                <Icon className="h-4 w-4 text-slate-700" />
                                            </span>
                                            <div>
                                                <h3 className="text-sm font-semibold text-slate-950">{section.title}</h3>
                                                <p className="text-xs text-slate-500">{section.summary}</p>
                                            </div>
                                        </div>
                                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusClasses(section.status)}`}>
                                            {section.status}
                                        </span>
                                    </div>
                                    <div className="mt-4 space-y-2">
                                        {section.metrics.slice(0, 4).map((metric) => (
                                            <div key={metric.key} className="flex items-center justify-between gap-3 text-sm">
                                                <span className="truncate text-slate-500">{metric.label}</span>
                                                <span className="font-semibold text-slate-900">{formatValue(metric.value, metric.format || 'number')}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {section.route && (
                                        <Button asChild variant="ghost" size="sm" className="mt-3 w-full justify-between">
                                            <Link to={section.route}>
                                                Open {section.title}
                                                <ArrowRight className="h-4 w-4" />
                                            </Link>
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <Card className="border-slate-200 shadow-sm">
                        <CardHeader>
                            <CardTitle>Recent Activity</CardTitle>
                            <CardDescription>Latest system movement across modules.</CardDescription>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {(dashboard?.recent_activity || []).map((activity: DashboardRecentActivity) => (
                                <div key={`${activity.title}-${activity.date}`} className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 p-3">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-900">{activity.title}</p>
                                        <p className="truncate text-xs text-slate-500">{activity.meta || '—'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-medium text-slate-700">{activity.status || 'Updated'}</p>
                                        <p className="text-xs text-slate-400">{formatRelativeTime(activity.date)}</p>
                                    </div>
                                </div>
                            ))}
                            {(dashboard?.recent_activity || []).length === 0 && (
                                <p className="text-sm text-slate-500">No recent activity for this data set.</p>
                            )}
                        </CardContent>
                    </Card>
                </>
            ) : (
                <>
                    <ExecutiveDrillLinks links={moduleDashboard?.drill_links || []} />

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
                        {moduleDashboard?.charts?.[0] && <ExecutiveChartCard chart={moduleDashboard.charts[0]} />}
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                        {(moduleDashboard?.charts || []).slice(1).map((chart) => (
                            <ExecutiveChartCard key={chart.id} chart={chart} />
                        ))}
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                        <Card className="border-slate-200 shadow-sm xl:col-span-1">
                            <CardHeader>
                                <CardTitle>Why Attention</CardTitle>
                                <CardDescription>Signals driving executive follow-up in this module.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {(moduleDashboard?.attention_items || []).length > 0 ? (
                                    moduleDashboard!.attention_items.map((item) => (
                                        <Link
                                            key={item.id}
                                            to={item.route || '/reports'}
                                            className={`block rounded-lg border p-3 ${severityClasses(item.severity)}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold">{item.title}</p>
                                                    <p className="mt-1 text-xs opacity-80">{item.detail}</p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">No attention items right now.</p>
                                )}
                            </CardContent>
                        </Card>

                        <div className="grid grid-cols-1 gap-6 xl:col-span-2 xl:grid-cols-2">
                            {(moduleDashboard?.top_lists || []).map((list) => (
                                <ExecutiveTopListCard key={list.id} list={list} />
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export const DashboardHome: React.FC = () => {
    const { user, hasPermission } = useAuth();
    const canSeeExecutiveDashboard = String(user?.role || '').toLowerCase() === 'admin' || hasPermission('reports.view');

    if (canSeeExecutiveDashboard) {
        return <ExecutiveDashboardHome />;
    }

    return <RoleWorkspaceHome />;
};
